import React, { useState } from 'react';
import { FiRefreshCw, FiUpload, FiCheckCircle, FiGlobe, FiFileText, FiAlertCircle, FiCheck, FiExternalLink } from 'react-icons/fi';
import Toggle from '../Toggle';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { toast } from '../Toast';
import { SettingsActions } from './ClientSettings';
import { WP_ACTIVITY, useClient } from './clientSettingsData';

interface Form {
    address: string;
    username: string;
    // empty = keep the stored one; only set after Replace the Password
    password: string;
    updatePosts: boolean;
    unpublish: boolean;
    investiWins: boolean;
}

// "https://www.49metals.com.au/" → "49metals.com.au"
const hostOf = (url: string) => url.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '');

const MASKED = '••••  ••••  ••••  ••••  ••••  ••••';

// Client settings › WordPress (spec 672:5812). Preview only: there is no WordPress sync yet, so tests, syncs and saves only toast.
const WordPressConnection: React.FC = () => {
    const { stock, short } = useClient();
    const site = stock?.websiteUrl ? stock.websiteUrl.replace(/\/+$/, '') : 'https://49metals.com.au';
    const initial: Form = { address: site, username: 'investi-sync', password: '', updatePosts: true, unpublish: true, investiWins: true };
    const [form, setForm] = useState<Form>(initial);
    const [saved, setSaved] = useState<Form>(initial);
    const [replacing, setReplacing] = useState(false);
    const [testing, setTesting] = useState(false);
    const [lastSync, setLastSync] = useState('4 minutes ago');
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);
    const host = hostOf(form.address);
    const endpoint = `${form.address.trim().replace(/\/+$/, '')}/wp-json/wp/v2/`;

    const set = <K extends keyof Form>(field: K, value: Form[K]) => setForm(f => ({ ...f, [field]: value }));

    const test = () => {
        setTesting(true);
        setTimeout(() => {
            setTesting(false);
            toast.info(`Preview only: ${host} wasn’t contacted`);
        }, 1200);
    };

    const sync = () => {
        setLastSync('just now');
        toast.info('Preview only: nothing was sent to WordPress');
    };

    const save = () => {
        setSaved({ ...form, password: '' });
        setForm(f => ({ ...f, password: '' }));
        setReplacing(false);
        toast.info('Preview only: this isn’t connected yet, so the WordPress settings were not saved');
    };

    const discard = () => {
        setForm(saved);
        setReplacing(false);
    };

    const actions = { dirty, onSave: save, onDiscard: discard };
    const testButton = (cls: string) => <button type="button" className={cls} onClick={test} disabled={testing}>
        <FiRefreshCw className={testing ? 'spin' : ''} /> {testing ? 'Testing…' : 'Test the Connection'}
    </button>;

    return <>
        <div className="page-header">
            <div>
                <h1>WordPress Connection</h1>
                <p>Investi writes news and media straight into the {short} website as real WordPress posts, and updates them when you change them here.</p>
            </div>
            <div className="action-top">
                {dirty
                    ? <SettingsActions {...actions} />
                    : <>
                        {testButton('btn-outline with-icon')}
                        <button type="button" className="btn-primary with-icon" onClick={sync}><FiUpload /> Sync Now</button>
                    </>}
            </div>
        </div>

        <div className="wp-status">
            <span className="icon-tile"><FiCheckCircle /></span>
            <div className="text">
                <strong>Connected to {host}</strong>
                <span>WordPress 6.6.2 · Investi plugin 3.1 · last sync {lastSync} · 24 posts published by Investi</span>
            </div>
        </div>

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiGlobe /></span>
                        <div className="text">
                            <h2>How Investi Signs In</h2>
                            <div className="hint">The WordPress REST API, with an application password — not your own login</div>
                        </div>
                    </div>

                    <label className="field">
                        <span className="label">WordPress address</span>
                        <input className="mono" type="url" value={form.address} onChange={e => set('address', e.target.value)} />
                    </label>
                    <label className="field">
                        <span className="label">REST API endpoint</span>
                        <input className="mono" value={endpoint} readOnly />
                        <span className="help">Investi checks this when you press Test the Connection.</span>
                    </label>
                    <label className="field">
                        <span className="label">WordPress username</span>
                        <input value={form.username} onChange={e => set('username', e.target.value)} autoComplete="off" />
                        <span className="help">Make a separate user in WordPress with the Editor role — never an administrator.</span>
                    </label>
                    <label className="field">
                        <span className="label">Application password</span>
                        {replacing
                            ? <input className="mono" type="password" value={form.password} placeholder="Paste the new application password" onChange={e => set('password', e.target.value)} autoComplete="new-password" autoFocus />
                            : <input className="mono" value={MASKED} readOnly />}
                        <span className="help">Users › Profile › Application Passwords in WordPress. It is shown once, so paste it here straight away.</span>
                    </label>

                    <div className="buttons">
                        {testButton('btn-outline with-icon small')}
                        <button type="button" className="btn-outline with-icon small" onClick={() => setReplacing(true)} disabled={replacing}><FiRefreshCw /> Replace the Password</button>
                    </div>

                    <div className="wp-note"><ShieldCheckIcon /> <p>An application password only works for the REST API and can be revoked in WordPress at any time without changing anyone’s login. Investi stores it encrypted and never shows it again.</p></div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiFileText /></span>
                        <div className="text">
                            <h2>Where Each Kind Goes</h2>
                            <div className="hint">Investi keeps them apart in WordPress, so directors never appear in your news feed and news never appears on the board page.</div>
                        </div>
                    </div>

                    <div className="destination">
                        <div className="dest-head"><strong>News &amp; Media</strong><span className="badge info">Posts</span></div>
                        <p>Articles, videos, podcasts and webinars — everything from Website Hub › News &amp; media.</p>
                        <code>{host}/news-media/{'{slug}'}/</code>
                        <dl>
                            <dt>WordPress category</dt><dd>News &amp; Media</dd>
                            <dt>Author shown</dt><dd>{short} Investor Relations</dd>
                            <dt>Media coverage</dt><dd>link only — no post is created</dd>
                        </dl>
                    </div>

                    <div className="destination people">
                        <div className="dest-head"><strong>Board &amp; Management</strong><span className="badge info">Custom post type · People</span></div>
                        <p>Each director and manager gets their own page, with the biography as the body of that page.</p>
                        <code>{host}/about-us/board-management/{'{name}'}/</code>
                        <dl>
                            <dt>Post type</dt><dd>investi_person — kept out of the blog</dd>
                            <dt>Body of the page</dt><dd>the biography you write in Investi</dd>
                            <dt>Also saved</dt><dd>role, committees, qualifications, photo, LinkedIn</dd>
                            <dt>Order</dt><dd>the order you set on the board page</dd>
                        </dl>
                    </div>

                    <div className="wp-note"><FiAlertCircle /> <p>People are a separate post type, so they never show in the blog, the news widget, search results for news, or an RSS feed of your announcements. Your theme can still style them like any other page.</p></div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <h2 className="card-title">Keeping Them in Step</h2>
                    <div className="step-rows">
                        <div className="setting-row">
                            <div className="text">
                                <div className="title">Update the post when I edit it here</div>
                                <div className="hint">Investi rewrites the WordPress post within a minute</div>
                            </div>
                            <Toggle label="Update the post when I edit it here" checked={form.updatePosts} onChange={v => set('updatePosts', v)} />
                        </div>
                        <div className="setting-row">
                            <div className="text">
                                <div className="title">Unpublish when I unpublish here</div>
                                <div className="hint">The post moves to Draft, it is never deleted</div>
                            </div>
                            <Toggle label="Unpublish when I unpublish here" checked={form.unpublish} onChange={v => set('unpublish', v)} />
                        </div>
                        <div className="setting-row">
                            <div className="text">
                                <div className="title">Investi wins if both change</div>
                                <div className="hint">If someone edits the post in WordPress, Investi overwrites it on the next update</div>
                            </div>
                            <Toggle label="Investi wins if both change" checked={form.investiWins} onChange={v => set('investiWins', v)} />
                        </div>
                    </div>
                    <div className="wp-note warn"><FiAlertCircle /> <p>Turn the last one off if your web team edits posts by hand. Investi then flags the difference instead of overwriting it.</p></div>
                </section>

                <section className="card">
                    <h2 className="card-title">Recent Activity</h2>
                    <div className="activity" tabIndex={0} role="region" aria-label="Recent activity">
                        {WP_ACTIVITY.map(a => <div key={a.title} className={a.kind}>
                            {a.kind === 'done' ? <FiCheck /> : a.kind === 'link' ? <FiExternalLink /> : <FiAlertCircle />}
                            <div className="text"><strong>{a.title}</strong><span>{a.note}</span></div>
                        </div>)}
                    </div>
                </section>
            </div>
        </div>
    </>;
};

export default WordPressConnection;
